import { getUserTimezone, setDefaultDateFormattingLocale } from '@mnd-frontend/dates';
import type { UserLocale } from '@mnd-frontend/graphql-schema';
import { setDefaultLocaleOnPrototypes } from '@mnd-frontend/utils';
import i18next from 'i18next';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { defaultLocale } from '../components/SetupProvider/i18n';

const UserLocaleAndTimezoneContext = createContext<{ locale: UserLocale; timezone: string }>({
  locale: defaultLocale,
  timezone: 'GMT',
});

export const UserLocaleAndTimezoneContextProvider = ({
  locale,
  timezone,
  children,
}: {
  locale: UserLocale | undefined;
  timezone: string | undefined;
  children: React.ReactNode;
}) => {
  const value = useMemo(
    () => ({
      locale: locale ?? defaultLocale,
      timezone: timezone ?? getUserTimezone(),
    }),
    [locale, timezone],
  );

  useEffect(() => {
    if (value.locale !== i18next.language) {
      i18next.changeLanguage(value.locale);
    }
    setDefaultLocaleOnPrototypes(value.locale);
    setDefaultDateFormattingLocale(value.locale);
    window.locale = value.locale;
    window.timezone = value.timezone;
  }, [value.locale, value.timezone]);

  return (
    <UserLocaleAndTimezoneContext.Provider value={value}>
      {children}
    </UserLocaleAndTimezoneContext.Provider>
  );
};

export const useUserLocaleAndTimezone = () => useContext(UserLocaleAndTimezoneContext);
